import React, { useState, useEffect } from 'react';
import { Star, Trash2, Plus, AlertCircle, CheckCircle, Loader2, Lock, LogOut } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const PWD_KEY = 'ecopra_admin_pwd';

const AdminAvis = () => {
  const { toast } = useToast();
  const [adminPassword, setAdminPassword] = useState(() => localStorage.getItem(PWD_KEY) || '');
  const [authChecked, setAuthChecked] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [loginInput, setLoginInput] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    location: 'Suisse',
    vehicle: '',
    rating: 5,
    text: '',
    savings: '',
    date: new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }),
    source: 'Site Ecopra Suisse'
  });

  // Vérifie le mot de passe stocké au chargement
  useEffect(() => {
    const verify = async () => {
      if (!adminPassword) {
        setAuthChecked(true);
        return;
      }
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password: adminPassword }),
        });
        if (res.ok) {
          setAuthed(true);
        } else {
          localStorage.removeItem(PWD_KEY);
          setAdminPassword('');
        }
      } catch (e) {
        // ignore
      } finally {
        setAuthChecked(true);
      }
    };
    verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: loginInput }),
      });
      if (res.ok) {
        localStorage.setItem(PWD_KEY, loginInput);
        setAdminPassword(loginInput);
        setAuthed(true);
        toast({ title: '✅ Connexion réussie', description: 'Bienvenue dans l\'espace admin.' });
      } else {
        toast({ title: '❌ Mot de passe incorrect', variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Erreur', description: 'Impossible de contacter le serveur.', variant: 'destructive' });
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(PWD_KEY);
    setAdminPassword('');
    setAuthed(false);
    setLoginInput('');
  };

  const fetchReviews = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews`);
      if (response.ok) {
        const data = await response.json();
        setReviews(data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des avis:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authed) {
      fetchReviews();
    }
  }, [authed]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Password': adminPassword,
        },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        toast({ title: '✅ Avis ajouté !', description: `L'avis de ${formData.name} a été publié.` });
        setFormData({
          name: '',
          location: 'Suisse',
          vehicle: '',
          rating: 5,
          text: '',
          savings: '',
          date: new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }),
          source: 'Site Ecopra Suisse'
        });
        fetchReviews();
      } else if (response.status === 401) {
        toast({ title: 'Session expirée', description: 'Veuillez vous reconnecter.', variant: 'destructive' });
        handleLogout();
      } else {
        throw new Error('Erreur lors de l\'ajout');
      }
    } catch (error) {
      toast({ title: '❌ Erreur', description: 'Impossible d\'ajouter l\'avis.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (reviewId, reviewName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'avis de ${reviewName} ?`)) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews/${reviewId}`, {
        method: 'DELETE',
        headers: { 'X-Admin-Password': adminPassword },
      });
      if (response.ok) {
        toast({ title: '🗑️ Avis supprimé' });
        fetchReviews();
      } else if (response.status === 401) {
        handleLogout();
      } else {
        throw new Error('Erreur');
      }
    } catch (error) {
      toast({ title: 'Erreur', description: 'Suppression impossible.', variant: 'destructive' });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'rating' ? parseInt(value) : value
    }));
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, index) => (
      <Star key={index} className={`w-5 h-5 ${index < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
    ));
  };

  // ── Écran de chargement ──
  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00BCD4]" />
      </div>
    );
  }

  // ── Écran de login ──
  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#7CB342]/10 to-[#00BCD4]/10 px-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full" data-testid="admin-login-card">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[#7CB342] to-[#00BCD4] mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800">Espace Administration</h1>
            <p className="text-sm text-gray-500 mt-2">Connectez-vous pour gérer les avis clients</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe</label>
              <input
                type="password"
                value={loginInput}
                onChange={(e) => setLoginInput(e.target.value)}
                required
                autoFocus
                data-testid="admin-password-input"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00BCD4] focus:border-transparent"
                placeholder="••••••••••••"
              />
            </div>
            <button
              type="submit"
              disabled={loginLoading || !loginInput}
              data-testid="admin-login-submit"
              className="w-full bg-gradient-to-r from-[#7CB342] to-[#00BCD4] text-white font-bold py-3 px-6 rounded-lg hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loginLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Lock className="w-5 h-5" />}
              Se connecter
            </button>
          </form>
          <p className="text-xs text-gray-400 text-center mt-6">
            Cette page est privée. Tentatives consignées.
          </p>
        </div>
      </div>
    );
  }

  // ── Page admin ──
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-[#7CB342] to-[#00BCD4] bg-clip-text text-transparent mb-2">
              Gestion des Avis Clients
            </h1>
            <p className="text-gray-600">Espace privé — ajout et modération des témoignages publiés sur le site.</p>
          </div>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-btn"
            className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 transition"
          >
            <LogOut className="w-4 h-4" /> Déconnexion
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Form Section */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center gap-2 mb-6">
              <Plus className="w-6 h-6 text-[#00BCD4]" />
              <h2 className="text-2xl font-bold text-gray-800">Ajouter un avis</h2>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom du client *</label>
                <input type="text" name="name" value={formData.name} onChange={handleInputChange} required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00BCD4]" placeholder="Ex: Jean Dupont" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Localisation *</label>
                  <input type="text" name="location" value={formData.location} onChange={handleInputChange} required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00BCD4]" placeholder="Ex: Sion, Genève" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Note *</label>
                  <select name="rating" value={formData.rating} onChange={handleInputChange} required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00BCD4]">
                    <option value="5">⭐⭐⭐⭐⭐ (5)</option>
                    <option value="4">⭐⭐⭐⭐ (4)</option>
                    <option value="3">⭐⭐⭐ (3)</option>
                    <option value="2">⭐⭐ (2)</option>
                    <option value="1">⭐ (1)</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Véhicule *</label>
                <input type="text" name="vehicle" value={formData.vehicle} onChange={handleInputChange} required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00BCD4]" placeholder="Ex: BMW X5 3.0d" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Témoignage *</label>
                <textarea name="text" value={formData.text} onChange={handleInputChange} required rows="5"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00BCD4] resize-none"
                  placeholder="Partagez l'expérience du client..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Économies réalisées *</label>
                <input type="text" name="savings" value={formData.savings} onChange={handleInputChange} required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00BCD4]" placeholder="Ex: 25% d'économie" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input type="text" name="date" value={formData.date} onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00BCD4]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                  <input type="text" name="source" value={formData.source} onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00BCD4]" />
                </div>
              </div>
              <button type="submit" disabled={submitting} data-testid="admin-add-review-submit"
                className="w-full bg-gradient-to-r from-[#7CB342] to-[#00BCD4] text-white font-bold py-3 px-6 rounded-lg hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Ajout en cours...</> : <><CheckCircle className="w-5 h-5" /> Publier l'avis</>}
              </button>
            </form>
          </div>

          {/* Reviews List Section */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Avis publiés ({reviews.length})</h2>
              <button onClick={fetchReviews} className="text-sm text-[#00BCD4] hover:text-[#7CB342] font-medium">🔄 Rafraîchir</button>
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#00BCD4]" /></div>
            ) : reviews.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Aucun avis pour le moment</p>
              </div>
            ) : (
              <div className="space-y-4 max-h-[600px] overflow-y-auto">
                {reviews.map((review) => (
                  <div key={review.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-semibold text-gray-800">{review.name}</h3>
                        <div className="flex items-center gap-1 mt-1">{renderStars(review.rating)}</div>
                      </div>
                      <button onClick={() => handleDelete(review.id, review.name)}
                        data-testid={`admin-delete-review-${review.id}`}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-colors" title="Supprimer">
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                    <p className="text-sm text-gray-600 mb-2 line-clamp-2">{review.text}</p>
                    <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                      <span>🚗 {review.vehicle}</span>
                      <span>📍 {review.location}</span>
                      <span>💰 {review.savings}</span>
                    </div>
                    <div className="text-xs text-gray-400 mt-2">{review.date} · {review.source}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-1">ℹ️ Comment ça marche</p>
              <p>Les clients soumettent leur avis via la page <code className="bg-blue-100 px-2 py-0.5 rounded">/temoignages</code>. Vous recevez l'avis par email à <strong>contact@ecopra.ch</strong> et pouvez l'ajouter ici manuellement après vérification.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAvis;
